/**
 * Hydrogen Toast Notification System
 *
 * A minimal, ephemeral notification system.
 * Creates a container on demand and appends toast messages that auto-expire.
 * Includes screen reader announcements via a live region.
 */
import { HydrogenError, warnOnce } from '../utils/errors.js';
// Singleton container reference for toast messages
let container = null;
// Singleton live region for screen reader announcements
let liveRegion = null;
// Timer ID for the screen reader announcement delay
let announceTimerId = null;
// Active toast timer IDs keyed by element reference, for cleanup on early removal
const activeToastTimers = new Map();
/**
 * Retrieves the lazy-loaded toast container.
 * Creates it if it doesn't exist.
 */
function getContainer() {
    if (!container) {
        container = document.createElement('div');
        container.className = 'toast-container';
        document.body.appendChild(container);
    }
    return container;
}
/**
 * Retrieves the lazy-loaded live region for screen reader announcements.
 * Creates it if it doesn't exist.
 */
function getLiveRegion() {
    if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.className = 'sr-only';
        document.body.appendChild(liveRegion);
    }
    return liveRegion;
}
/**
 * Announces a message to screen readers via the live region.
 */
function announceToScreenReader(message) {
    const region = getLiveRegion();
    // Clear any pending announcement
    if (announceTimerId !== null) {
        clearTimeout(announceTimerId);
    }
    // Clear and re-set to ensure announcement triggers
    region.textContent = '';
    // Use setTimeout to ensure the DOM update is detected
    announceTimerId = setTimeout(() => {
        region.textContent = message;
        announceTimerId = null;
    }, 50);
}
// Default durations by toast type (in ms)
const TYPE_DURATIONS = {
    success: 3000,
    error: 5000,
    warning: 4000,
    info: 3000,
};
// Valid toast types
const VALID_TYPES = ['success', 'error', 'warning', 'info'];
/**
 * Displays a toast message.
 *
 * @throws {HydrogenError} If message is not a string
 */
export function toast(message, type = 'info', duration) {
    // Validate message
    if (typeof message !== 'string') {
        throw new HydrogenError('ToastError', 'toast() message must be a string', {
            expected: "toast('Your message here')",
            received: message,
            docPath: 'notifications#toast',
        });
    }
    if (message.length === 0) {
        throw new HydrogenError('ToastError', 'toast() message cannot be empty', {
            expected: "toast('Your message here')",
            received: "''",
            docPath: 'notifications#toast',
        });
    }
    // Warn about invalid type (don't throw - use fallback)
    let toastType = 'info';
    if (VALID_TYPES.includes(type)) {
        toastType = type;
    }
    else {
        warnOnce(`toast-invalid-type-${type}`, `toast() received invalid type '${type}', using 'info'. Valid types: ${VALID_TYPES.join(', ')}`);
    }
    // Validate duration if provided
    let actualDurationArg = duration;
    if (actualDurationArg !== undefined &&
        (typeof actualDurationArg !== 'number' || actualDurationArg <= 0)) {
        warnOnce('toast-invalid-duration', `toast() duration must be a positive number, using default. Received: ${actualDurationArg}`);
        actualDurationArg = undefined;
    }
    const c = getContainer();
    const actualDuration = actualDurationArg ?? TYPE_DURATIONS[toastType] ?? 3000;
    // Create toast element
    const el = document.createElement('div');
    el.className = `toast toast-${toastType}`;
    el.textContent = message;
    // Announce to screen readers
    announceToScreenReader(message);
    // Append to DOM (triggers CSS animation if configured)
    c.appendChild(el);
    // Schedule removal with tracked timer IDs
    const fadeTimerId = setTimeout(() => {
        // Fade out
        el.style.opacity = '0';
        el.style.transform = 'translateY(10px)';
        // Remove from DOM after transition completes
        const removeTimerId = setTimeout(() => {
            el.remove();
            activeToastTimers.delete(el);
        }, 300);
        // Update tracked timer to the removal timer
        activeToastTimers.set(el, removeTimerId);
    }, actualDuration);
    // Track the fade timer so it can be cleared on early removal
    activeToastTimers.set(el, fadeTimerId);
}
/**
 * Clears all active toast timers and removes pending toasts.
 * Call this during teardown/cleanup to prevent memory leaks.
 */
export function destroyToasts() {
    // Clear announcement timer
    if (announceTimerId !== null) {
        clearTimeout(announceTimerId);
        announceTimerId = null;
    }
    // Clear all active toast timers
    for (const timerId of activeToastTimers.values()) {
        clearTimeout(timerId);
    }
    activeToastTimers.clear();
    // Remove singleton elements
    if (container?.parentNode) {
        container.remove();
        container = null;
    }
    if (liveRegion?.parentNode) {
        liveRegion.remove();
        liveRegion = null;
    }
}
//# sourceMappingURL=toast.js.map